import { useState } from 'react';
import { Droplets } from 'lucide-react';
import { usePets } from '../../../contexts/PetsContext';
import { useHydrationGroups } from '../../../contexts/HydrationGroupsContext';
import { useHydrationStatus } from '../../../hooks/useHydrationStatus';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { formatList } from '../../../lib/format';

/**
 * Botón "Dar agua" — equivalente al FeedingButton del lado de comida.
 *
 * Diferencias con FeedingButton:
 *  - No hay stock que descontar (el agua no se agota).
 *  - Si el balde es compartido, registra a TODAS las pets del balde con
 *    un solo log (event "watering" con payload.affectedPetIds[]).
 *
 * Flujos:
 *  - Caso feliz (no recientemente regado): click → registra directo.
 *  - Recientemente regado (<25% del intervalo): modal de confirmación.
 *  - Sin pets en el balde: nunca debería pasar si el botón se renderiza
 *    desde un pet detail; defensivo igual.
 *

 * Props:
 *  - group: HydrationGroup completo.
 *  - variant: 'compact' (PetCard) | 'full' (PetDetailPage / InventoryPage).
 *  - fullWidth: si true, el botón ocupa todo el ancho del contenedor. El variant
 *    'full' siempre es fullWidth; en 'compact' el caller decide (ej. PetCard usa
 *    fullWidth dentro de un wrapper flex-1 para layout 50/50).
 */
const WaterButton = ({ group, variant = 'compact', fullWidth = false }) => {
  const { pets } = usePets();
  const { waterHydrationGroup } = useHydrationGroups();
  const toast = useToast();
  const status = useHydrationStatus(group);

  const [confirmingRecent, setConfirmingRecent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pets activas asociadas a este balde. Snapshot tomado en cada render — el
  // service hace el commit transaccional con el array que le pasamos.
  const affectedPets = pets
    .filter((p) => p.hydrationGroupId === group?.id && !p.deletedAt)
    .map((p) => ({ id: p.id, name: p.name }));

  const noPets = affectedPets.length === 0;

  const executeWater = async () => {
    try {
      setSubmitting(true);
      const result = await waterHydrationGroup(group.id, affectedPets);
      const verb = result.affectedCount === 1 ? 'tomó' : 'tomaron';
      toast.success(
        `${formatList(result.affectedNames)} ${verb} agua del balde "${group.name}".`
      );
      setConfirmingRecent(false);
    } catch (err) {
      toast.error(err.message || 'No se pudo registrar la toma de agua.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    if (noPets) {
      toast.info('Asociá al menos una mascota a este balde para registrar el agua.');
      return;
    }
    if (status.isRecentlyWatered) {
      setConfirmingRecent(true);
      return;
    }
    executeWater();
  };

  const button = (
    <Button
      variant="water"
      size={variant === 'full' ? 'lg' : 'md'}
      leftIcon={Droplets}
      onClick={handleClick}
      isLoading={submitting}
      disabled={submitting || noPets}
      fullWidth={variant === 'full' || fullWidth}
    >
      {variant === 'full' ? 'Dar agua' : 'Agua'}
    </Button>
  );

  return (
    <>
      {button}

      <Modal
        isOpen={confirmingRecent}
        onClose={() => !submitting && setConfirmingRecent(false)}
        title="Recién tomaron agua"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            El balde "{group?.name}" fue refrescado hace poco. ¿Querés registrar
            otra toma igual?
          </p>
          <div className="rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Va a quedar registrado para {formatList(affectedPets.map((p) => p.name))}.
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmingRecent(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              isLoading={submitting}
              onClick={executeWater}
            >
              Registrar igual
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WaterButton;
